import { Component, type ErrorInfo, type ReactNode } from 'react'
import { Button } from '@/components/ui/button'

interface ErrorBoundaryProps {
  children: ReactNode
  /**
   * When this value changes while the fallback is showing, the boundary resets
   * and re-renders its children. Pass the route pathname so navigating away
   * from a crashed page recovers automatically — unlike a React `key`, this
   * never remounts healthy children on navigation.
   */
  resetKey?: string
}

interface ErrorBoundaryState {
  hasError: boolean
}

/**
 * App-wide error boundary: catches any render/lifecycle throw in its subtree and
 * shows a usable Dutch fallback instead of unmounting the whole SPA (which would
 * leave only the fixed VONK background and a dead router — an inescapable screen).
 *
 * Place it around the routed `<Outlet/>` with `resetKey={location.pathname}`, so
 * navigating away auto-recovers; "Opnieuw proberen" retries in place, and "Terug
 * naar start" does a hard navigation that works even when the in-memory router
 * is broken. (Do NOT use a React `key` for this: that remounts the entire
 * subtree — layouts, gates, sidebar — on every navigation, which flashes the
 * shell.)
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false }

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true }
  }

  componentDidUpdate(prevProps: ErrorBoundaryProps): void {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false })
    }
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface the crash for diagnostics (Sentry/console); the UI degrades calmly.
    console.error('ErrorBoundary caught a render error:', error, info.componentStack)
  }

  private handleRetry = (): void => {
    this.setState({ hasError: false })
  }

  private handleHome = (): void => {
    // A hard navigation escapes even when the SPA router is broken.
    window.location.assign('/')
  }

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children

    return (
      <div className="flex min-h-svh items-center justify-center p-6">
        <div className="vonk-card flex max-w-md flex-col items-center gap-3 text-center">
          <h1 className="font-heading text-lg font-semibold">Er ging iets mis</h1>
          <p className="text-muted-foreground">
            Deze pagina kon niet worden geladen. Probeer het opnieuw of ga terug naar het
            startscherm.
          </p>
          <div className="mt-1 flex flex-wrap justify-center gap-2">
            <Button onClick={this.handleRetry}>Opnieuw proberen</Button>
            <Button variant="outline" onClick={this.handleHome}>
              Terug naar start
            </Button>
          </div>
        </div>
      </div>
    )
  }
}
